import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Check } from "lucide-react";
import { api } from "../../services/api";
import { getAppLanguage } from "../../utils/appLanguage";
import { getUpgradePopupText } from "../../utils/upgradePopupText";
import { VARIANT_COPY, getPopupVariant } from "../../utils/upgradePopupCopy";
import {
  UPGRADE_POPUP_EVENT,
  getPopupSessionId,
  reportAnalyticsEvent,
  type UpgradePopupRequest,
} from "../../utils/upgradePopup";

interface PopupCopy {
  title: string;
  subtitle: string;
  countryLine?: string | null;
}

/**
 * The one upgrade popup. It listens for `requestUpgradePopup(...)` and asks the
 * server whether it may show. The server applies every rule (plan, trigger,
 * 1 per session, 3 per day, 30 minute wait, first session) and we show
 * nothing unless it says yes. Any error means "show nothing".
 *
 * Copy by moment (see utils/upgradePopupCopy.ts):
 *   Free limit (conversation 12) -> "Continue Your Journey"
 *   Locked insight               -> "Available in Pro"
 *   anything else                -> the server's own title and subtitle
 *
 * Mounted once, inside ChatProvider. It never opens by itself.
 * Pro users never see it: the server answers show:false for them.
 */
const UpgradePopup: React.FC = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState<(UpgradePopupRequest & { copy: PopupCopy }) | null>(null);
  const busy = useRef(false);

  useEffect(() => {
    const onRequest = async (event: Event) => {
      const request = (event as CustomEvent<UpgradePopupRequest>).detail;
      if (!request?.trigger || busy.current || open || !localStorage.getItem("token")) return;
      busy.current = true;
      try {
        const { data } = await api.post(
          "/backend/monetization/upgrade-popup/check",
          { ...request, sessionId: getPopupSessionId() },
          { skipAuthRedirect: true },
        );
        if (data?.show && data.copy?.title) setOpen({ ...request, copy: data.copy });
      } catch {
        /* no popup */
      } finally {
        busy.current = false;
      }
    };
    window.addEventListener(UPGRADE_POPUP_EVENT, onRequest);
    return () => window.removeEventListener(UPGRADE_POPUP_EVENT, onRequest);
  }, [open]);

  const dismiss = useCallback(() => {
    if (!open) return;
    api
      .post("/backend/monetization/upgrade-popup/dismiss", { trigger: open.trigger, feature: open.feature, placement: open.placement }, { skipAuthRedirect: true })
      .catch(() => {});
    setOpen(null);
  }, [open]);

  const accept = useCallback(() => {
    if (!open) return;
    reportAnalyticsEvent("upsell_clicked", { trigger: open.trigger, feature: open.feature || "", placement: open.placement || "" });
    setOpen(null);
    navigate("/subscription");
  }, [open, navigate]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && dismiss();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, dismiss]);

  if (!open) return null;

  const ui = getUpgradePopupText(getAppLanguage());
  const variant = getPopupVariant(open.trigger);
  const fixed = variant === "generic" ? null : VARIANT_COPY[variant];
  const title = fixed ? fixed.title : open.copy.title;
  const cta = fixed ? fixed.cta : ui.cta;
  const dismissLabel = fixed ? fixed.dismiss : ui.dismiss;
  const soft = "var(--astria-text-secondary,#c8bfd1)";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="upgrade-popup-title"
      data-variant={variant}
      onClick={dismiss}
      style={{ position: "fixed", inset: 0, zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, background: "rgba(0,0,0,.65)", backdropFilter: "blur(4px)" }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ width: "100%", maxWidth: 400, borderRadius: 24, padding: "28px 24px 24px", textAlign: "center", background: "var(--astria-surface,#1a131f)", color: "var(--astria-text-primary,#f7f3fa)", border: "1px solid rgba(245,217,154,.35)", boxShadow: "0 16px 48px -12px rgba(167,139,255,.5)" }}
      >
        <Sparkles size={32} style={{ margin: "0 auto 14px", color: "#f5d99a" }} aria-hidden="true" />
        <h2 id="upgrade-popup-title" style={{ fontSize: 21, fontWeight: 700, margin: "0 0 10px", lineHeight: 1.25 }}>{title}</h2>

        {fixed ? (
          fixed.body.map((line, i) => (
            <p key={line} style={{ margin: i === fixed.body.length - 1 ? "0 0 20px" : "0 0 4px", fontSize: i === 0 ? 15 : 13, color: soft, opacity: i === 0 ? 1 : 0.85 }}>{line}</p>
          ))
        ) : (
          <>
            <p style={{ margin: "0 0 4px", fontSize: 14, color: soft }}>{open.copy.subtitle}</p>
            {open.copy.countryLine && <p style={{ margin: "8px 0 0", fontSize: 13, color: soft, opacity: 0.85 }}>{open.copy.countryLine}</p>}
            <div style={{ height: 16 }} />
          </>
        )}

        {fixed && (
          <ul style={{ listStyle: "none", padding: 0, margin: "0 0 22px", textAlign: "left", display: "flex", flexDirection: "column", gap: 8 }}>
            {fixed.benefits.map((benefit) => (
              <li key={benefit} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14 }}>
                <Check size={15} style={{ flexShrink: 0, color: "#f5d99a", background: "rgba(245,217,154,.15)", borderRadius: "50%", padding: 2 }} aria-hidden="true" />
                {benefit}
              </li>
            ))}
          </ul>
        )}

        <button
          type="button"
          onClick={accept}
          style={{ marginBottom: 10, width: "100%", padding: "13px 16px", borderRadius: 14, border: 0, cursor: "pointer", fontWeight: 700, fontSize: 15, background: "linear-gradient(135deg,#f5d99a,#e0b866)", color: "#1a1233" }}
        >
          {cta}
        </button>
        <button
          type="button"
          onClick={dismiss}
          style={{ width: "100%", padding: "11px 16px", borderRadius: 14, border: 0, cursor: "pointer", background: "transparent", color: soft, fontSize: 14 }}
        >
          {dismissLabel}
        </button>
      </div>
    </div>
  );
};

export default UpgradePopup;
